/* __next_internal_client_entry_do_not_use__ default auto */ import { _ as _tagged_template_literal } from "@swc/helpers/_/_tagged_template_literal";
function _templateObject() {
    const data = _tagged_template_literal([
        "\n  0% {\n    opacity: 1;\n  }\n\n  50% {\n    opacity: 0.4;\n  }\n\n  100% {\n    opacity: 1;\n  }\n"
    ]);
    _templateObject = function() {
        return data;
    };
    return data;
}
function _templateObject1() {
    const data = _tagged_template_literal([
        "\n  0% {\n    transform: translateX(-100%);\n  }\n\n  50% {\n    /* +0.5s of delay between each loop */\n    transform: translateX(100%);\n  }\n\n  100% {\n    transform: translateX(100%);\n  }\n"
    ]);
    _templateObject1 = function() {
        return data;
    };
    return data;
}
function _templateObject2() {
    const data = _tagged_template_literal([
        "\n        animation: ",
        " 2s ease-in-out 0.5s infinite;\n      "
    ]);
    _templateObject2 = function() {
        return data;
    };
    return data;
}
function _templateObject3() {
    const data = _tagged_template_literal([
        "\n        &::after {\n          animation: ",
        " 2s linear 0.5s infinite;\n        }\n      "
    ]);
    _templateObject3 = function() {
        return data;
    };
    return data;
}
import * as React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import composeClasses from '@mui/utils/composeClasses';
import { alpha, unstable_getUnit as getUnit, unstable_toUnitless as toUnitless } from "../styles/index.js";
import { keyframes, css, styled } from "../zero-styled/index.js";
import memoTheme from "../utils/memoTheme.js";
import { useDefaultProps } from "../DefaultPropsProvider/index.js";
import { getSkeletonUtilityClass } from "./skeletonClasses.js";
import { jsx as _jsx } from "react/jsx-runtime";
const useUtilityClasses = (ownerState)=>{
    const { classes, variant, animation, hasChildren, width, height } = ownerState;
    const slots = {
        root: [
            'root',
            variant,
            animation,
            hasChildren && 'withChildren',
            hasChildren && !width && 'fitContent',
            hasChildren && !height && 'heightAuto'
        ]
    };
    return composeClasses(slots, getSkeletonUtilityClass, classes);
};
const pulseKeyframe = keyframes(_templateObject());
const waveKeyframe = keyframes(_templateObject1());
// This implementation is for supporting both Styled-components v4+ and Pigment CSS.
// A global animation has to be created here for Styled-components v4+ (https://github.com/styled-components/styled-components/blob/main/packages/styled-components/src/utils/errors.md#12).
// which can be done by checking typeof indeterminate1Keyframe !== 'string' (at runtime, Pigment CSS transform keyframes`` to a string).
const pulseAnimation = typeof pulseKeyframe !== 'string' ? css(_templateObject2(), pulseKeyframe) : null;
const waveAnimation = typeof waveKeyframe !== 'string' ? css(_templateObject3(), waveKeyframe) : null;
const SkeletonRoot = styled('span', {
    name: 'MuiSkeleton',
    slot: 'Root',
    overridesResolver: (props, styles)=>{
        const { ownerState } = props;
        return [
            styles.root,
            styles[ownerState.variant],
            ownerState.animation !== false && styles[ownerState.animation],
            ownerState.hasChildren && styles.withChildren,
            ownerState.hasChildren && !ownerState.width && styles.fitContent,
            ownerState.hasChildren && !ownerState.height && styles.heightAuto
        ];
    }
})(memoTheme((param)=>{
    let { theme } = param;
    const radiusUnit = getUnit(theme.shape.borderRadius) || 'px';
    const radiusValue = toUnitless(theme.shape.borderRadius);
    return {
        display: 'block',
        // Create a "on paper" color with sufficient contrast retaining the color
        backgroundColor: theme.vars ? theme.vars.palette.Skeleton.bg : alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.11 : 0.13),
        height: '1.2em',
        variants: [
            {
                props: {
                    variant: 'text'
                },
                style: {
                    marginTop: 0,
                    marginBottom: 0,
                    height: 'auto',
                    transformOrigin: '0 55%',
                    transform: 'scale(1, 0.60)',
                    borderRadius: "".concat(radiusValue).concat(radiusUnit, "/").concat(Math.round(radiusValue / 0.6 * 10) / 10).concat(radiusUnit),
                    '&:empty:before': {
                        content: '"\\00a0"'
                    }
                }
            },
            {
                props: {
                    variant: 'circular'
                },
                style: {
                    borderRadius: '50%'
                }
            },
            {
                props: {
                    variant: 'rounded'
                },
                style: {
                    borderRadius: (theme.vars || theme).shape.borderRadius
                }
            },
            {
                props: (param)=>{
                    let { ownerState } = param;
                    return ownerState.hasChildren;
                },
                style: {
                    '& > *': {
                        visibility: 'hidden'
                    }
                }
            },
            {
                props: (param)=>{
                    let { ownerState } = param;
                    return ownerState.hasChildren && !ownerState.width;
                },
                style: {
                    maxWidth: 'fit-content'
                }
            },
            {
                props: (param)=>{
                    let { ownerState } = param;
                    return ownerState.hasChildren && !ownerState.height;
                },
                style: {
                    height: 'auto'
                }
            },
            {
                props: {
                    animation: 'pulse'
                },
                style: pulseAnimation || {
                    animation: "".concat(pulseKeyframe, " 2s ease-in-out 0.5s infinite")
                }
            },
            {
                props: {
                    animation: 'wave'
                },
                style: {
                    position: 'relative',
                    overflow: 'hidden',
                    /* Fix bug in Safari https://bugs.webkit.org/show_bug.cgi?id=68196 */ WebkitMaskImage: '-webkit-radial-gradient(white, black)',
                    '&::after': {
                        background: "linear-gradient(\n                90deg,\n                transparent,\n                ".concat((theme.vars || theme).palette.action.hover, ",\n                transparent\n              )"),
                        content: '""',
                        position: 'absolute',
                        transform: 'translateX(-100%)' /* Avoid flash during server-side hydration */ ,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        top: 0
                    }
                }
            },
            {
                props: {
                    animation: 'wave'
                },
                style: waveAnimation || {
                    '&::after': {
                        animation: "".concat(waveKeyframe, " 2s linear 0.5s infinite")
                    }
                }
            }
        ]
    };
}));
const Skeleton = /*#__PURE__*/ React.forwardRef(function Skeleton(inProps, ref) {
    const props = useDefaultProps({
        props: inProps,
        name: 'MuiSkeleton'
    });
    const { animation = 'pulse', className, component = 'span', height, style, variant = 'text', width, ...other } = props;
    const ownerState = {
        ...props,
        animation,
        component,
        variant,
        hasChildren: Boolean(other.children)
    };
    const classes = useUtilityClasses(ownerState);
    return /*#__PURE__*/ _jsx(SkeletonRoot, {
        as: component,
        ref: ref,
        className: clsx(classes.root, className),
        ownerState: ownerState,
        ...other,
        style: {
            width,
            height,
            ...style
        }
    });
});
"production" !== "production" ? Skeleton.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │    To update them, edit the d.ts file and run `pnpm proptypes`.     │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * The animation.
   * If `false` the animation effect is disabled.
   * @default 'pulse'
   */ animation: PropTypes.oneOf([
        'pulse',
        'wave',
        false
    ]),
    /**
   * Optional children to infer width and height from.
   */ children: PropTypes.node,
    /**
   * Override or extend the styles applied to the component.
   */ classes: PropTypes.object,
    /**
   * @ignore
   */ className: PropTypes.string,
    /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */ component: PropTypes.elementType,
    /**
   * Height of the skeleton.
   * Useful when you don't want to adapt the skeleton to a text element but for instance a card.
   */ height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    /**
   * @ignore
   */ style: PropTypes.object,
    /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */ sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.bool
        ])),
        PropTypes.func,
        PropTypes.object
    ]),
    /**
   * The type of content that will be rendered.
   * @default 'text'
   */ variant: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'circular',
            'rectangular',
            'rounded',
            'text'
        ]),
        PropTypes.string
    ]),
    /**
   * Width of the skeleton.
   * Useful when the skeleton is inside an inline element with no width of its own.
   */ width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
} : void 0;
export default Skeleton;
